import "../../../components/welcomeScreen/welcomeWizard.scss";

import { map } from "lodash";
import PropTypes from "prop-types";
import PubSub from "pubsub-js";
import React from "react";

import { withContainerContext } from "../../../components/container/ContainerContext";
import FormButton from "../../../components/form/FormButton";
import FromDropdown from "../../../components/form/FormDropdown";
import Icon from "../../../components/interface/Icon";
import ModalCheckboxGrid from "../../../components/modal/ModalCheckboxGrid";
import ModalDescription from "../../../components/modal/ModalDescription";
import ModalField from "../../../components/modal/ModalField";
import ModalGroup from "../../../components/modal/ModalGroup";
import ModalRow from "../../../components/modal/ModalRow";
import pubSubTopics from "../../../constants/pubSubTopics";
import syncApplications, { syncApplicationNamesForConstant } from "../../../constants/syncApplications";
import { nativeCommsBroadcastMessageTypes } from "../../../nativeComms";
import { PropTypeShapes } from "../../../types";
import pathUtils from "../../../utils/pathUtils";
import preprocessorQuery from "../../../utils/preprocessorQuery";

const isIos = preprocessorQuery.isIos();

const onLinkClick = path => {
  PubSub.publish(pubSubTopics.sendNativeMessage, {
    msg: nativeCommsBroadcastMessageTypes.OPEN_EXTERNAL_LINK,
    path
  });
};

const renderDescriptionGroup = useProps => {
  const isUlysses = useProps.selectedProject ? useProps.selectedProject.toLowerCase() === "ulysses" : false;
  const app = isUlysses ? syncApplications.ULYSSES : syncApplications.SCRIVENER;

  const appLabel = useProps.selectedProject ? syncApplicationNamesForConstant[app] : "Scrivener or Ulysses";

  const narrativeLabel = isUlysses
    ? "You must open Sync Settings and choose a folder to sync directly to your narrative"
    : "Your timeline's narrative will now sync directly with your Manuscript folder";

  const keywordsLabel = isUlysses
    ? "Optionally sync relationships as keywords instead of notes"
    : "Optionally sync relationships as keywords instead of custom metadata";

  return (
    <ModalGroup
      key="descriptionGroup"
      label={!isIos && useProps.availableProjects.length > 1 ? "Features" : isIos ? "Desktop syncing features" : null}
    >
      {!isIos && useProps.availableProjects.length <= 1 ? (
        <ModalRow key="syncing">
          <ModalDescription>
            <span>
              <Icon color="iconMid" icon="sidebarSync" />
              {useProps.availableProjects.length > 0
                ? `Your existing sync settings will be imported. Use the Sync panel to customise.`
                : `Use the Sync panel to enable syncing.`}
            </span>
          </ModalDescription>
        </ModalRow>
      ) : null}
      {isIos ? (
        <ModalRow key="syncing">
          <ModalDescription>
            <span>Use Aeon Timeline for Desktop to sync with {appLabel}</span>
          </ModalDescription>
        </ModalRow>
      ) : (
        <ModalRow key="scrivener">
          <ModalDescription>
            <b>{appLabel}</b>
          </ModalDescription>
        </ModalRow>
      )}
      <ModalRow key="scrivenerDesc">
        <ModalField>
          <ModalCheckboxGrid columns={1}>
            <div className="welcomeWizardAttributeItem">
              <Icon icon="tick" />
              <div className="welcomeWizardAttributeItemLabel">{narrativeLabel}</div>
            </div>
            <div className="welcomeWizardAttributeItem">
              <Icon icon="tick" />
              <div className="welcomeWizardAttributeItemLabel">
                Choose to sync entities such as Characters and Locations to other folders
              </div>
            </div>
            <div className="welcomeWizardAttributeItem">
              <Icon icon="tick" />
              <div className="welcomeWizardAttributeItemLabel">{keywordsLabel}</div>
            </div>
          </ModalCheckboxGrid>
        </ModalField>
      </ModalRow>
    </ModalGroup>
  );
};

const renderProjectSelectGroup = useProps => {
  const projectOptions = map(useProps.availableProjects, p => {
    const filename = pathUtils.fileNameFromPath(p);
    return {
      label: filename,
      value: p,
      description: p
    };
  });

  return (
    <ModalGroup key="projectSelectGroup" label={`Choose Synced Project`}>
      <ModalRow key="description">
        <ModalDescription>
          <span>
            Aeon Timeline 3 only allows syncing with a single writing project. Please select the project you wish to
            {isIos ? " sync Aeon Timeline for Desktop" : " proceed"} with:
          </span>
        </ModalDescription>
      </ModalRow>
      <ModalRow key="includeNarrative">
        <ModalField>
          <FromDropdown
            options={projectOptions}
            value={useProps.selectedProject}
            onChange={useProps.onProjectChange}
            showDescription={true}
          />
        </ModalField>
      </ModalRow>
    </ModalGroup>
  );
};

const renderWebsiteLink = _useProps => {
  return (
    <ModalGroup key="desktop">
      <ModalRow>
        <ModalDescription>
          <FormButton
            onClick={() => onLinkClick("https://timeline.app/desktop")}
            label="Get Aeon Timeline for Desktop"
            linkStyle={true}
          />
        </ModalDescription>
      </ModalRow>
    </ModalGroup>
  );
};

const EnableSyncPage = props => {
  return [
    renderDescriptionGroup(props),
    props.includeNarrative && props.availableProjects.length > 1 ? renderProjectSelectGroup(props) : null,
    isIos ? renderWebsiteLink(props) : null
  ];
};

EnableSyncPage.propTypes = {
  availableProjects: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedProject: PropTypes.string,
  onProjectChange: PropTypes.func.isRequired,
  onBackClick: PropTypes.func.isRequired,
  onNextClick: PropTypes.func.isRequired,
  includeNarrative: PropTypes.bool,
  // From containerContext
  containerRect: PropTypeShapes.flatRect.isRequired
};

renderProjectSelectGroup.propTypes = EnableSyncPage.propTypes;

export default withContainerContext(EnableSyncPage);
